import Text from '@/components/Text';
import {
    flattenZodUnionErrors,
    formatZodErrors,
    resolveHandlebarsAsJson,
    TemplateSchema,
    tryCatch
} from '@squaredup/utilities';
import DisplayJsonUi from 'components/forms/jsonForms/DisplayJsonUi';
import { ResolvedUIConfig } from 'components/forms/jsonForms/resolveAutocompleteOptions';
import stringify from 'fast-json-stable-stringify';
import { FormProvider, useForm } from 'react-hook-form';

interface UIPreviewProps {
    datasourceConfig: object;
    uiConfig?: ResolvedUIConfig[];
    uiConfigMapping?: Record<string, any>;
}

export const UIPreview: React.FC<UIPreviewProps> = ({ datasourceConfig, uiConfig = [], uiConfigMapping = {} }) => {
    const previewForm = useForm();

    const mappedDataSourceConfig =
        uiConfigMapping != null
            ? tryCatch(
                  () => ({
                      ...datasourceConfig,
                      ...JSON.parse(
                          resolveHandlebarsAsJson(stringify(uiConfigMapping), previewForm.watch(), '{}', false) ?? '{}'
                      )
                  }),
                  (e) => `Error resolving UI config mapping: ${(e as Error).message}`
              )()
            : undefined;

    const uiConfigParse = TemplateSchema.safeParse(uiConfig);

    return (
        <div className='flex flex-col max-w-[400px] min-w-80 flex-1 gap-4 pl-4 ml-4 py-8 bg-componentBackgroundSecondary scroll-overflow max-h-full'>
            <Text.H2 className='truncate text-textPrimary'>UI Preview</Text.H2>

            <FormProvider {...previewForm}>
                <Text.H4>UI configuration</Text.H4>
                <div className='p-4 border rounded-lg border-outlinePrimary'>
                    {uiConfig == null || uiConfig.length === 0 || !uiConfigParse.success ? (
                        uiConfigParse.success ? (
                            <span className='text-gray-300'>No UI.json template.</span>
                        ) : (
                            <pre className='break-all whitespace-pre-wrap text-statusErrorPrimary'>
                                {JSON.stringify(
                                    flattenZodUnionErrors(uiConfigParse.error).map(formatZodErrors),
                                    null,
                                    4
                                )}
                            </pre>
                        )
                    ) : (
                        <DisplayJsonUi includeDisplayNameField={false} formFields={uiConfig} />
                    )}
                </div>

                <Text.H4>Output (Mapped Configuration)</Text.H4>
                <div className='p-4 border rounded-lg border-outlinePrimary'>
                    {!uiConfigParse.success ? (
                        <span className='text-gray-300'>No UI configuration mapping, or mapping is invalid.</span>
                    ) : (
                        <pre className='break-all whitespace-pre-wrap'>
                            {JSON.stringify(mappedDataSourceConfig, null, 4)}
                        </pre>
                    )}
                </div>
            </FormProvider>
        </div>
    );
};
